import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { CartItem, Product, PaymentMethod } from '@/types';
import i18n from '@/i18n';

export interface MurabahaCustomer {
  id: string;
  full_name: string;
  phone: string;
  murabaha_limit: number;
  limit_expires_at: string | null;
}

export interface CartSession {
  id: string;
  name: string;
  items: CartItem[];
  paymentMethod: PaymentMethod;
  murabahaCustomer: MurabahaCustomer | null;
  createdAt: string;
}

export interface CartDraft extends CartSession {
  savedAt: string;
}

export const MAX_SESSIONS = 6;

const SESSIONS_KEY = 'hisob-pos-sessions';
const DRAFTS_KEY = 'hisob-pos-drafts';

const newId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const makeSession = (name: string): CartSession => ({
  id: newId(),
  name,
  items: [],
  paymentMethod: 'cash',
  murabahaCustomer: null,
  createdAt: new Date().toISOString(),
});

const sessionTotal = (s: { items: CartItem[] }) => s.items.reduce((sum, c) => sum + c.total, 0);

interface AppContextType {
  // Active session shortcuts (backwards compatible API)
  cart: CartItem[];
  addToCart: (product: Product, price: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, qty: number) => void;
  setItemUnit: (productId: string, unit: string) => void;
  clearCart: () => void;
  cartTotal: number;
  murabahaCustomer: MurabahaCustomer | null;
  setMurabahaCustomer: (customer: MurabahaCustomer) => void;
  clearMurabahaCustomer: () => void;
  remainingLimit: number | null;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (m: PaymentMethod) => void;

  // Multi-session (parallel carts)
  sessions: CartSession[];
  activeSessionId: string;
  createSession: (name?: string) => void;
  switchSession: (id: string) => void;
  closeSession: (id: string) => void;
  renameSession: (id: string, name: string) => void;
  sessionTotalOf: (id: string) => number;

  // Drafts
  drafts: CartDraft[];
  saveAsDraft: (name?: string) => void;
  restoreDraft: (draftId: string) => void;
  deleteDraft: (draftId: string) => void;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [sessions, setSessions] = useState<CartSession[]>(() => {
    try {
      const raw = localStorage.getItem(SESSIONS_KEY);
      const parsed = raw ? (JSON.parse(raw) as CartSession[]) : [];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch { /* ignore corrupt storage */ }
    return [makeSession(i18n.t('misc_app.customer_n', { n: 1 }))];
  });

  const [activeSessionId, setActiveSessionId] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(`${SESSIONS_KEY}:active`);
      if (raw) return raw;
    } catch { /* ignore */ }
    return '';
  });

  const [drafts, setDrafts] = useState<CartDraft[]>(() => {
    try {
      const raw = localStorage.getItem(DRAFTS_KEY);
      const parsed = raw ? (JSON.parse(raw) as CartDraft[]) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });

  // Ensure active session id always points to an existing session
  useEffect(() => {
    if (sessions.length === 0) {
      const s = makeSession(i18n.t('misc_app.customer_n', { n: 1 }));
      setSessions([s]);
      setActiveSessionId(s.id);
      return;
    }
    if (!sessions.some(s => s.id === activeSessionId)) {
      setActiveSessionId(sessions[0].id);
    }
  }, [sessions, activeSessionId]);

  useEffect(() => {
    try {
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
      if (activeSessionId) localStorage.setItem(`${SESSIONS_KEY}:active`, activeSessionId);
    } catch { /* storage full / unavailable */ }
  }, [sessions, activeSessionId]);

  useEffect(() => {
    try { localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts)); } catch { /* ignore */ }
  }, [drafts]);

  const active = useMemo(
    () => sessions.find(s => s.id === activeSessionId) ?? sessions[0],
    [sessions, activeSessionId],
  );

  const patchActive = useCallback((patch: (s: CartSession) => CartSession) => {
    setSessions(prev => {
      const targetId = prev.some(s => s.id === activeSessionId) ? activeSessionId : prev[0]?.id;
      return prev.map(s => (s.id === targetId ? patch(s) : s));
    });
  }, [activeSessionId]);

  const addToCart = useCallback((product: Product, price: number) => {
    patchActive(s => {
      const existing = s.items.find(c => c.product.id === product.id);
      if (existing) {
        return {
          ...s,
          items: s.items.map(c => c.product.id === product.id
            ? { ...c, quantity: c.quantity + 1, total: (c.quantity + 1) * c.unit_price }
            : c),
        };
      }
      const units = product.allowed_units || [];
      return {
        ...s,
        items: [...s.items, {
          product, quantity: 1, unit_price: price, total: price,
          selected_unit: units.length > 0 ? units[0] : (product.unit || 'dona'),
        }],
      };
    });
  }, [patchActive]);

  const removeFromCart = useCallback((productId: string) => {
    patchActive(s => ({ ...s, items: s.items.filter(c => c.product.id !== productId) }));
  }, [patchActive]);

  const updateQuantity = useCallback((productId: string, qty: number) => {
    patchActive(s => qty <= 0
      ? { ...s, items: s.items.filter(c => c.product.id !== productId) }
      : { ...s, items: s.items.map(c => c.product.id === productId ? { ...c, quantity: qty, total: qty * c.unit_price } : c) });
  }, [patchActive]);

  const setItemUnit = useCallback((productId: string, unit: string) => {
    patchActive(s => ({
      ...s,
      items: s.items.map(c => c.product.id === productId ? { ...c, selected_unit: unit } : c),
    }));
  }, [patchActive]);

  const clearCart = useCallback(() => {
    patchActive(s => ({ ...s, items: [] }));
  }, [patchActive]);

  const setPaymentMethod = useCallback((m: PaymentMethod) => {
    patchActive(s => ({ ...s, paymentMethod: m }));
  }, [patchActive]);

  const setMurabahaCustomer = useCallback((customer: MurabahaCustomer) => {
    patchActive(s => ({
      ...s,
      murabahaCustomer: customer,
      name: customer.full_name || s.name,
    }));
  }, [patchActive]);

  const clearMurabahaCustomer = useCallback(() => {
    patchActive(s => ({ ...s, murabahaCustomer: null }));
  }, [patchActive]);

  // ---- Sessions ----
  const createSession = useCallback((name?: string) => {
    setSessions(prev => {
      if (prev.length >= MAX_SESSIONS) return prev;
      const s = makeSession(name || i18n.t('misc_app.customer_n', { n: prev.length + 1 }));
      setActiveSessionId(s.id);
      return [...prev, s];
    });
  }, []);

  const switchSession = useCallback((id: string) => setActiveSessionId(id), []);

  const closeSession = useCallback((id: string) => {
    setSessions(prev => {
      const next = prev.filter(s => s.id !== id);
      if (next.length === 0) {
        const s = makeSession(i18n.t('misc_app.customer_n', { n: 1 }));
        setActiveSessionId(s.id);
        return [s];
      }
      if (id === activeSessionId) setActiveSessionId(next[0].id);
      return next;
    });
  }, [activeSessionId]);

  const renameSession = useCallback((id: string, name: string) => {
    setSessions(prev => prev.map(s => s.id === id ? { ...s, name: name.trim() || s.name } : s));
  }, []);

  const sessionTotalOf = useCallback((id: string) => {
    const s = sessions.find(x => x.id === id);
    return s ? sessionTotal(s) : 0;
  }, [sessions]);

  // ---- Drafts ----
  const saveAsDraft = useCallback((name?: string) => {
    const current = sessions.find(s => s.id === activeSessionId) ?? sessions[0];
    if (!current || current.items.length === 0) return;
    const draft: CartDraft = {
      ...current,
      id: newId(),
      name: (name || current.name).trim(),
      savedAt: new Date().toISOString(),
    };
    setDrafts(prev => [draft, ...prev].slice(0, 30));
    setSessions(prev => {
      const next = prev.filter(s => s.id !== current.id);
      if (next.length === 0) {
        const s = makeSession(i18n.t('misc_app.customer_n', { n: 1 }));
        setActiveSessionId(s.id);
        return [s];
      }
      setActiveSessionId(next[0].id);
      return next;
    });
  }, [sessions, activeSessionId]);

  const restoreDraft = useCallback((draftId: string) => {
    const draft = drafts.find(d => d.id === draftId);
    if (!draft) return;
    setSessions(prev => {
      if (prev.length >= MAX_SESSIONS) return prev;
      const restored: CartSession = {
        id: newId(),
        name: draft.name,
        items: draft.items,
        paymentMethod: draft.paymentMethod,
        murabahaCustomer: draft.murabahaCustomer,
        createdAt: new Date().toISOString(),
      };
      setActiveSessionId(restored.id);
      setDrafts(d => d.filter(x => x.id !== draftId));
      return [...prev, restored];
    });
  }, [drafts]);

  const deleteDraft = useCallback((draftId: string) => {
    setDrafts(prev => prev.filter(d => d.id !== draftId));
  }, []);

  const cart = active?.items ?? [];
  const cartTotal = cart.reduce((s, c) => s + c.total, 0);
  const murabahaCustomer = active?.murabahaCustomer ?? null;
  const remainingLimit = murabahaCustomer ? murabahaCustomer.murabaha_limit - cartTotal : null;

  return (
    <AppContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, setItemUnit, clearCart, cartTotal,
      murabahaCustomer, setMurabahaCustomer, clearMurabahaCustomer, remainingLimit,
      paymentMethod: active?.paymentMethod ?? 'cash', setPaymentMethod,
      sessions, activeSessionId: active?.id ?? '', createSession, switchSession, closeSession, renameSession, sessionTotalOf,
      drafts, saveAsDraft, restoreDraft, deleteDraft,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
}
